import {
  CuttingSize,
  CreateCuttingSizeRequest,
  UpdateCuttingSizeRequest,
  CuttingSizeFilters,
  CuttingSizeListResponse,
} from '@/types/cutting';

import { api } from '@/api/axios';

// API Response Types (based on backend DTOs)
interface ApiCuttingSizeResponse {
  id: string;
  code: string;
  size: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface ApiStandardResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

// Mapping functions
const mapApiCuttingSizeToFrontend = (apiCuttingSize: ApiCuttingSizeResponse): CuttingSize => {
  return {
    id: apiCuttingSize.id,
    code: apiCuttingSize.code,
    size: apiCuttingSize.size,
    note: apiCuttingSize.note || '',
    createdAt: apiCuttingSize.createdAt,
    updatedAt: apiCuttingSize.updatedAt,
  };
};

const mapFrontendCuttingSizeToApi = (cuttingSize: CreateCuttingSizeRequest | Partial<CreateCuttingSizeRequest>) => {
  return {
    size: cuttingSize.size || '',
    note: cuttingSize.note || '',
  };
};

export const cuttingSizeApi = {
  // Get cutting sizes list with filters and pagination
  getCuttingSizes: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: CuttingSizeFilters
  ): Promise<CuttingSizeListResponse> => {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      // Add filters
      if (filters?.search) {
        params.append('search', filters.search);
      }
      
      if (filters?.sortBy) {
        params.append('sort_by', filters.sortBy);
      }
      
      if (filters?.sortDesc !== undefined) {
        params.append('sort_desc', filters.sortDesc.toString());
      }

      const response = await api.get<ApiPaginatedResponse<ApiCuttingSizeResponse>>(
        `/cutting-sizes?${params.toString()}`
      );

      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      const { data: cuttingSizes, total, page: currentPage, pageSize: currentPageSize } = response.data;

      return {
        data: cuttingSizes.map(mapApiCuttingSizeToFrontend),
        total,
        page: currentPage,
        pageSize: currentPageSize,
      };
    } catch (error: any) {
      console.error('Error fetching cutting sizes:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch cutting sizes');
    }
  },

  // Get single cutting size by code
  getCuttingSize: async (code: string): Promise<CuttingSize> => {
    try {
      const response = await api.get<ApiStandardResponse<ApiCuttingSizeResponse>>(`/cutting-sizes/${code}`);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiCuttingSizeToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error fetching cutting size:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch cutting size');
    }
  },



  // Create new cutting size
  createCuttingSize: async (data: CreateCuttingSizeRequest): Promise<CuttingSize> => {
    try {
      const apiData = mapFrontendCuttingSizeToApi(data);
      const response = await api.post<ApiStandardResponse<ApiCuttingSizeResponse>>('/cutting-sizes', apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiCuttingSizeToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error creating cutting size:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to create cutting size');
    }
  },

  // Update cutting size
  updateCuttingSize: async (data: UpdateCuttingSizeRequest): Promise<CuttingSize> => {
    try {
      const { code, ...updateData } = data;
      const apiData = mapFrontendCuttingSizeToApi(updateData);
      const response = await api.put<ApiStandardResponse<ApiCuttingSizeResponse>>(`/cutting-sizes/${code}`, apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiCuttingSizeToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error updating cutting size:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to update cutting size');
    }
  },

  // Delete cutting size
  deleteCuttingSize: async (code: string): Promise<void> => {
    try {
      await api.delete(`/cutting-sizes/${code}`);
    } catch (error: any) {
      console.error('Error deleting cutting size:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to delete cutting size');
    }
  },
};
